/**
 * DoserneKlaer — Klær shop + mine mål + bestillinger for Doserne Academy.
 *
 * Three tabs live behind a single page:
 *   1. Shop         — catalog cards with a size/color picker sheet
 *   2. Mine mål     — size profile (used to pre-fill the shop sheet)
 *   3. Bestillinger — the user's order history; admins additionally see
 *                     "Innkomne bestillinger" from all workers with a
 *                     "Marker som levert" button per order.
 *
 * All admin controls (catalog CRUD, deliver, reopen) are guarded server-
 * side too; the UI just hides them from non-admins.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Minus, Trash2, ShoppingBag, ShoppingCart, Ruler, ListChecks, X, Save,
  Package, CheckCircle2, RotateCcw, Settings2, ChevronRight, ChevronLeft, Pencil, Send, ChevronUp, ChevronDown, Upload, Star, ZoomIn,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("no-NO", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

// -----------------------------------------------------------------
// CATALOG ADMIN modal — CRUD over the catalog
// -----------------------------------------------------------------
function CatalogAdmin({ onClose, authHeader, onChanged }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // item obj being edited (null = closed)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/clothing/catalog?include_inactive=true`, authHeader());
      setItems(data.items || []);
      setCategories(data.categories || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const moveCategory = async (idx, dir) => {
    const next = [...categories];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setCategories(next);
    try {
      await axios.put(`${API}/doserne/clothing/category-order`, { order: next }, authHeader());
      onChanged?.();
    } catch { /* ignore */ }
  };

  const emptyItem = () => ({
    id: null,
    name: "",
    slug: "",
    category: "",
    size_type: "custom",
    size_options: [],
    has_color: false,
    color_options: [],
    image_url: "",
    images: [],
    notes: "",
    sort_order: 100,
    active: true,
  });

  const save = async (draft) => {
    setBusy(true);
    try {
      if (draft.id) {
        await axios.put(`${API}/doserne/clothing/catalog/${draft.id}`, draft, authHeader());
        toast.success(t("klaer.catalog.item_saved"));
      } else {
        await axios.post(`${API}/doserne/clothing/catalog`, draft, authHeader());
        toast.success(t("klaer.catalog.item_created"));
      }
      setEditing(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.catalog.save_failed"));
    }
    setBusy(false);
  };

  const remove = async (item) => {
    if (!window.confirm(t("klaer.catalog.confirm_delete_permanent") + ` "${item.name}"`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/doserne/clothing/catalog/${item.id}?permanent=true`, authHeader());
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.catalog.save_failed"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="catalog-admin-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-3xl h-[90vh] sm:h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Admin</div>
            <h2 className="text-xl font-black text-slate-900">{t("klaer.catalog.admin_title")}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(emptyItem())}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-sm font-black"
              style={{ color: "#fff" }}
              data-testid="catalog-add-btn"
            >
              <Plus className="w-4 h-4" /> {t("klaer.catalog.new_item")}
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
            {categories.length > 1 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="category-order-section">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-2">{t("klaer.catalog.order_categories")}</div>
                <div className="space-y-1.5">
                  {categories.map((cat, idx) => (
                    <div key={cat} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5" data-testid={`category-order-row-${cat}`}>
                      <span className="text-xs font-black text-slate-400 w-5">{idx + 1}</span>
                      <span className="flex-1 text-sm font-bold text-slate-800">{cat}</span>
                      <button onClick={() => moveCategory(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded-md hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center text-slate-600" data-testid={`category-up-${cat}`}><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => moveCategory(idx, 1)} disabled={idx === categories.length - 1} className="w-7 h-7 rounded-md hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center text-slate-600" data-testid={`category-down-${cat}`}><ChevronDown className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <tr>
                  <th className="text-left px-3 py-2">{t("klaer.catalog.col_name")}</th>
                  <th className="text-left px-3 py-2">{t("klaer.catalog.col_sizes")}</th>
                  <th className="text-left px-3 py-2">{t("klaer.catalog.col_colors")}</th>
                  <th className="px-3 py-2 w-24">{t("klaer.catalog.col_status")}</th>
                  <th className="px-3 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100" data-testid={`catalog-row-${it.slug}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900">{it.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{it.slug}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{it.size_options.join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{it.color_options.join(", ") || "—"}</td>
                    <td className="px-3 py-2">
                      {it.active ? (
                        <span className="text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">{t("klaer.catalog.status_active")}</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-black text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5">{t("klaer.catalog.status_hidden")}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditing({ ...it })} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid={`catalog-edit-${it.slug}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(it)} className="w-8 h-8 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>

        {editing && (
          <CatalogEditor
            draft={editing}
            setDraft={setEditing}
            categories={[...new Set(items.map((i) => i.category).filter(Boolean))].sort()}
            authHeader={authHeader}
            onClose={() => setEditing(null)}
            onSave={() => save(editing)}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
}

function CatalogEditor({ draft, setDraft, categories = [], authHeader, onClose, onSave, busy }) {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const [confirmIdx, setConfirmIdx] = useState(null);
  const setField = (k, v) => setDraft((cur) => ({ ...cur, [k]: v }));
  const images = draft.images && draft.images.length ? draft.images : (draft.image_url ? [draft.image_url] : []);
  const setImages = (arr) => setDraft((cur) => ({ ...cur, images: arr, image_url: arr[0] || "" }));
  const moveImage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const arr = [...images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setImages(arr);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/doserne/clothing/admin/upload-image`, form, {
        ...authHeader(),
        headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      if (data.url) { setImages([...images, data.url]); toast.success(t("klaer.catalog.image_uploaded")); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("klaer.catalog.save_failed"));
    }
    setUploading(false);
  };
  return (
    <div className="absolute inset-0 bg-white flex flex-col rounded-t-2xl sm:rounded-2xl" data-testid="catalog-editor">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-black text-slate-900">{draft.id ? t("klaer.catalog.edit_item") : t("klaer.catalog.new_item")}</h3>
        <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_name")}</label>
          <input value={draft.name} onChange={(e) => setField("name", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="editor-name" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_category")}</label>
          <input
            value={draft.category || ""}
            onChange={(e) => setField("category", e.target.value)}
            list="klaer-category-options"
            placeholder={t("klaer.catalog.category_placeholder")}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="editor-category"
          />
          <datalist id="klaer-category-options">
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_sizes")}</label>
          <input
            value={draft.size_options.join(", ")}
            onChange={(e) => setField("size_options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="S, M, L, XL"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
            data-testid="editor-sizes"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
            <input type="checkbox" checked={draft.has_color} onChange={(e) => setField("has_color", e.target.checked)} data-testid="editor-has-color" />
            {t("klaer.catalog.field_has_color")}
          </label>
        </div>
        {draft.has_color && (
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_colors")}</label>
            <input
              value={draft.color_options.join(", ")}
              onChange={(e) => setField("color_options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder={t("klaer.catalog.color_placeholder")}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="editor-colors"
            />
          </div>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_image")}</label>
          <div className="flex items-center gap-2 flex-wrap">
            {images.map((src, i) => (
              <div key={i} className="w-20 h-20 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 relative" data-testid={`editor-thumb-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-0.5 left-0.5 inline-flex items-center gap-0.5 text-[8px] font-black text-white bg-emerald-600 rounded px-1 py-0.5" data-testid="editor-primary-badge"><Star className="w-2.5 h-2.5" />{t("klaer.catalog.primary")}</span>
                )}
                {confirmIdx === i ? (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center gap-1.5">
                    <button type="button" onClick={() => { setImages(images.filter((_, j) => j !== i)); setConfirmIdx(null); }} className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center" data-testid={`editor-confirm-remove-${i}`} title={t("common.delete")}><CheckCircle2 className="w-3.5 h-3.5 text-white" /></button>
                    <button type="button" onClick={() => setConfirmIdx(null)} className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center" data-testid={`editor-cancel-remove-${i}`}><X className="w-3.5 h-3.5 text-white" /></button>
                  </div>
                ) : (
                  <>
                    <button type="button" onClick={() => setConfirmIdx(i)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center" data-testid={`editor-remove-image-${i}`} title={t("klaer.catalog.remove_image")}>
                      <X className="w-3 h-3 text-white" />
                    </button>
                    {images.length > 1 && (
                      <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-black/40">
                        <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} className="flex-1 flex justify-center py-0.5 text-white disabled:opacity-30" data-testid={`editor-move-left-${i}`}><ChevronLeft className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} className="flex-1 flex justify-center py-0.5 text-white disabled:opacity-30" data-testid={`editor-move-right-${i}`}><ChevronRight className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-500 flex flex-col items-center justify-center gap-0.5 cursor-pointer text-slate-500 flex-shrink-0" data-testid="editor-upload-btn">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-[9px] font-bold text-center leading-tight">{uploading ? t("klaer.catalog.uploading") : t("klaer.catalog.upload_image")}</span>
              <input type="file" accept="image/*" onChange={uploadImage} className="hidden" disabled={uploading} data-testid="editor-image-file" />
            </label>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("klaer.catalog.field_note")}</label>
          <textarea rows={2} value={draft.notes} onChange={(e) => setField("notes", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-slate-800 w-40">{t("klaer.catalog.field_order")}</label>
          <input type="number" value={draft.sort_order} onChange={(e) => setField("sort_order", Number(e.target.value) || 100)} className="w-24 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-center" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" checked={draft.active} onChange={(e) => setField("active", e.target.checked)} data-testid="editor-active" />
          {t("klaer.catalog.show_in_shop")}
        </label>
      </div>
      <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">{t("common.cancel")}</button>
        <button
          onClick={onSave}
          disabled={busy || !draft.name.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
          style={{ color: "#fff" }}
          data-testid="editor-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t("common.save")}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SIZE PROFILE tab
// -----------------------------------------------------------------
function SizeProfileTab({ catalog, authHeader }) {
  const { t } = useLang();
  const [profile, setProfile] = useState({ sizes: {}, body: {}, color_pref: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/doserne/clothing/profile`, authHeader());
        if (alive) setProfile({ sizes: data.sizes || {}, body: data.body || {}, color_pref: data.color_pref || "" });
      } catch { /* ignore */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [authHeader]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/doserne/clothing/profile`, profile, authHeader());
      toast.success(t("klaer.profile.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.profile.save_failed"));
    }
    setSaving(false);
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="klaer-profile-tab">
      {/* Size per catalog item */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-lg font-black text-slate-900 mb-1">{t("klaer.profile.title_sizes")}</h3>
        <p className="text-xs text-slate-500 mb-4">
          {t("klaer.profile.hint_sizes")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {catalog.filter((c) => c.active).map((item) => (
            <div key={item.id} className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-800 w-40 truncate">{item.name}</label>
              {item.size_options.length > 0 ? (
                <select
                  value={profile.sizes[item.slug] || ""}
                  onChange={(e) => setProfile((p) => ({ ...p, sizes: { ...p.sizes, [item.slug]: e.target.value } }))}
                  className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                  data-testid={`profile-size-${item.slug}`}
                >
                  <option value="">{t("klaer.profile.select")}</option>
                  {item.size_options.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input
                  value={profile.sizes[item.slug] || ""}
                  onChange={(e) => setProfile((p) => ({ ...p, sizes: { ...p.sizes, [item.slug]: e.target.value } }))}
                  className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm"
                  placeholder="—"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Body measurements */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-lg font-black text-slate-900 mb-1">{t("klaer.profile.title_body")}</h3>
        <p className="text-xs text-slate-500 mb-4">{t("klaer.profile.hint_body")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: "height_cm", label: t("klaer.profile.height") },
            { key: "waist_cm", label: t("klaer.profile.waist") },
            { key: "leg_cm", label: t("klaer.profile.leg") },
            { key: "shoe_eu", label: t("klaer.profile.shoe_eu") },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{label}</label>
              <input
                type="number"
                value={profile.body[key] ?? ""}
                onChange={(e) => setProfile((p) => ({ ...p, body: { ...p.body, [key]: e.target.value ? Number(e.target.value) : null } }))}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm"
                data-testid={`profile-body-${key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Color preference */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-lg font-black text-slate-900 mb-1">{t("klaer.profile.title_color")}</h3>
        <input
          value={profile.color_pref}
          onChange={(e) => setProfile((p) => ({ ...p, color_pref: e.target.value }))}
          placeholder={t("klaer.profile.color_placeholder")}
          className="w-full sm:w-64 px-3 py-2 border border-slate-300 rounded-lg text-sm mt-2"
          data-testid="profile-color-pref"
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
          style={{ color: "#fff" }}
          data-testid="profile-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t("klaer.profile.save_btn")}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SHOP tab — catalog cards + order sheet
// -----------------------------------------------------------------
function ShopTab({ catalog, categories: categoryOrder = [], authHeader, profile, onAddToCart }) {
  const { t } = useLang();
  const [sheet, setSheet] = useState(null); // {item, size, color, quantity, note}
  const [activeCat, setActiveCat] = useState("__all__");

  const activeItems = catalog.filter((c) => c.active);
  const present = [...new Set(activeItems.map((c) => c.category).filter(Boolean))];
  // Use admin-defined order; fall back to alphabetical for any not listed.
  const categories = [
    ...categoryOrder.filter((c) => present.includes(c)),
    ...present.filter((c) => !categoryOrder.includes(c)).sort(),
  ];
  const visibleItems = activeCat === "__all__"
    ? activeItems
    : activeItems.filter((c) => (c.category || "") === activeCat);

  const openSheet = (item) => {
    setSheet({
      item,
      size: profile?.sizes?.[item.slug] || item.size_options[0] || "",
      color: profile?.color_pref && item.color_options.includes(profile.color_pref) ? profile.color_pref : (item.color_options[0] || ""),
      quantity: 1,
      note: "",
    });
  };

  return (
    <div data-testid="klaer-shop-tab">
      {categories.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-1 -mx-1 px-1 no-scrollbar" data-testid="klaer-category-chips">
          {[{ key: "__all__", label: t("klaer.shop.all_categories") }, ...categories.map((c) => ({ key: c, label: c }))].map((chip) => (
            <button
              key={chip.key}
              onClick={() => setActiveCat(chip.key)}
              className={`flex-shrink-0 text-xs font-black px-3.5 py-1.5 rounded-full border transition whitespace-nowrap ${
                activeCat === chip.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`klaer-chip-${chip.key}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {visibleItems.map((item) => (
          <button
            key={item.id}
            onClick={() => openSheet(item)}
            className="group text-left bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-md rounded-xl p-3 transition flex items-center gap-3"
            data-testid={`shop-card-${item.slug}`}
          >
            <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-8 h-8 text-slate-400 group-hover:text-emerald-500 transition" />
              )}
              {item.images && item.images.length > 1 && (
                <span className="absolute bottom-0.5 right-0.5 text-[9px] font-black text-white bg-black/60 rounded px-1" data-testid={`shop-card-count-${item.slug}`}>+{item.images.length - 1}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-slate-900 truncate">{item.name}</div>
              {item.notes && <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{item.notes}</div>}
              <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                {item.size_options.slice(0, 5).map((s) => (
                  <span key={s} className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{s}</span>
                ))}
                {item.size_options.length > 5 && (
                  <span className="text-[10px] text-slate-400">+{item.size_options.length - 5}</span>
                )}
              </div>
              {item.has_color && item.color_options.length > 0 && (
                <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                  <span className="uppercase tracking-widest font-black">{t("klaer.shop.colors_label")}</span>
                  <span>{item.color_options.slice(0, 3).join(" · ")}{item.color_options.length > 3 && "…"}</span>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-50 group-hover:bg-emerald-600 text-emerald-700 group-hover:text-white flex items-center justify-center transition">
              <Plus className="w-4 h-4" />
            </div>
          </button>
        ))}
      </div>

      {sheet && (
        <OrderSheet
          sheet={sheet}
          setSheet={setSheet}
          onClose={() => setSheet(null)}
          onSubmit={() => {
            onAddToCart({
              item_id: sheet.item.id,
              item_name: sheet.item.name,
              image_url: sheet.item.image_url || "",
              size: sheet.size,
              color: sheet.color,
              quantity: Math.max(1, Number(sheet.quantity) || 1),
              note: sheet.note,
            });
            toast.success(t("klaer.cart.added_toast", { name: sheet.item.name }));
            setSheet(null);
          }}
        />
      )}
    </div>
  );
}

function ShopGallery({ images }) {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const ref = useRef(null);
  if (!images || images.length === 0) return null;
  const onScroll = () => {
    const el = ref.current;
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  return (
    <div className="relative bg-slate-100" data-testid="shop-gallery">
      <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
        {images.map((src, i) => (
          <button key={i} type="button" onClick={() => setZoom(true)} className="w-full flex-shrink-0 snap-center relative" data-testid={`shop-gallery-img-${i}`}>
            <img src={src} alt="" className="w-full aspect-square object-cover" />
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center"><ZoomIn className="w-4 h-4 text-white" /></span>
          </button>
        ))}
      </div>
      {images.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
          {images.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? "bg-white shadow" : "bg-white/50"}`} />
          ))}
        </div>
      )}
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={() => setZoom(false)} data-testid="shop-lightbox">
          <button type="button" onClick={() => setZoom(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10" data-testid="lightbox-close"><X className="w-6 h-6 text-white" /></button>
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar w-full h-full items-center" onClick={(e) => e.stopPropagation()}>
            {images.map((src, i) => (
              <div key={i} className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-4">
                <img src={src} alt="" className="max-w-full max-h-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderSheet({ sheet, setSheet, onClose, onSubmit }) {
  const { t } = useLang();
  const { item } = sheet;
  const gallery = item.images && item.images.length ? item.images : (item.image_url ? [item.image_url] : []);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="order-sheet">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {gallery.length > 0 && <ShopGallery images={gallery} />}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">{t("klaer.order.kicker")}</div>
            <h2 className="text-xl font-black text-slate-900">{item.name}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {item.size_options.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("klaer.order.size")}</label>
              <div className="flex flex-wrap gap-1.5">
                {item.size_options.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSheet((cur) => ({ ...cur, size: s }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${sheet.size === s ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                    style={sheet.size === s ? { color: "#fff" } : {}}
                    data-testid={`order-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {item.has_color && item.color_options.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("klaer.order.color")}</label>
              <div className="flex flex-wrap gap-1.5">
                {item.color_options.map((c) => (
                  <button
                    key={c}
                    onClick={() => setSheet((cur) => ({ ...cur, color: c }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${sheet.color === c ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                    style={sheet.color === c ? { color: "#fff" } : {}}
                    data-testid={`order-color-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-slate-800">{t("klaer.order.quantity")}</label>
            <input
              type="number"
              min={1}
              value={sheet.quantity}
              onChange={(e) => setSheet((cur) => ({ ...cur, quantity: e.target.value }))}
              className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-center"
              data-testid="order-quantity"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("klaer.order.note")}</label>
            <textarea
              rows={2}
              value={sheet.note}
              onChange={(e) => setSheet((cur) => ({ ...cur, note: e.target.value }))}
              placeholder={t("klaer.order.note_placeholder")}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="order-note"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">{t("common.cancel")}</button>
          <button
            onClick={onSubmit}
            disabled={!sheet.size && item.size_options.length > 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
            style={{ color: "#fff" }}
            data-testid="order-submit-btn"
          >
            <Plus className="w-4 h-4" /> {t("klaer.cart.add_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// CART — persisted per user. Primary source of truth is the server
// (`/doserne/clothing/cart`) so a worker can start a cart on their
// phone and finish it on desktop. `localStorage` acts as an offline
// cache: we hydrate from it instantly on mount, then reconcile with
// the server. Every mutation triggers a debounced PUT.
// -----------------------------------------------------------------
const CART_KEY = (uid) => `DOSERNE_KLAER_CART_V1_${uid || "anon"}`;
const CART_PUT_DEBOUNCE_MS = 600;

function useCart(userId, authHeader) {
  const [items, setItems] = useState([]);
  const [synced, setSynced] = useState(false); // false until first server GET returns
  const debounceRef = useRef(null);
  const skipNextPutRef = useRef(true); // don't PUT immediately on hydrate

  // 1) Hydrate instantly from localStorage so the UI has something to show.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CART_KEY(userId));
      setItems(raw ? (JSON.parse(raw) || []) : []);
    } catch { setItems([]); }
    skipNextPutRef.current = true; // hydrate is not a user edit
    setSynced(false);
  }, [userId]);

  // 2) Reconcile with the server. Server wins if it has items (multi-device
  //    handoff); otherwise if we have local items, push them up.
  useEffect(() => {
    if (!userId || !authHeader) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/clothing/cart`, authHeader());
        if (!alive) return;
        const serverItems = Array.isArray(data?.items) ? data.items : [];
        if (serverItems.length) {
          skipNextPutRef.current = true; // adopt server state without echoing back
          setItems(serverItems);
        } else {
          // Server empty: keep whatever we hydrated locally (may still be [])
          // and let the debounced writer flush it up if non-empty.
          skipNextPutRef.current = false;
        }
      } catch { /* offline / auth error — stay on local cache */ }
      if (alive) setSynced(true);
    })();
    return () => { alive = false; };
  }, [userId, authHeader]);

  // 3) Persist locally on every change (offline-first cache).
  useEffect(() => {
    try { localStorage.setItem(CART_KEY(userId), JSON.stringify(items)); } catch { /* quota */ }
  }, [items, userId]);

  // 4) Debounced PUT to server on every change (skip the hydrate + server-adopt).
  useEffect(() => {
    if (!userId || !authHeader) return undefined;
    if (skipNextPutRef.current) { skipNextPutRef.current = false; return undefined; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      axios.put(`${API}/doserne/clothing/cart`, { items }, authHeader()).catch(() => { /* offline — retry next edit */ });
    }, CART_PUT_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [items, userId, authHeader]);

  const add = useCallback((line) => {
    setItems((cur) => {
      // Merge if same item + size + color + note (bump qty)
      const idx = cur.findIndex((l) =>
        l.item_id === line.item_id && l.size === line.size &&
        l.color === line.color && (l.note || "") === (line.note || "")
      );
      if (idx >= 0) {
        const merged = [...cur];
        merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + line.quantity };
        return merged;
      }
      return [...cur, { ...line, id: `${line.item_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }];
    });
  }, []);

  const removeLine = useCallback((lineId) => {
    setItems((cur) => cur.filter((l) => l.id !== lineId));
  }, []);

  const setQty = useCallback((lineId, qty) => {
    setItems((cur) => cur.map((l) => l.id === lineId ? { ...l, quantity: Math.max(1, qty) } : l));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return { items, add, removeLine, setQty, clear, synced };
}

function CartSheet({ cart, onClose, authHeader, onSubmitted }) {
  const { t } = useLang();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const total = cart.items.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);

  const submit = async () => {
    if (!cart.items.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/clothing/orders`, {
        items: cart.items.map((l) => ({
          item_id: l.item_id,
          item_name: l.item_name,
          size: l.size,
          color: l.color,
          quantity: Math.max(1, Number(l.quantity) || 1),
          note: l.note || "",
        })),
        note,
      }, authHeader());
      toast.success(t("klaer.cart.sent_toast", { count: total }));
      cart.clear();
      setNote("");
      onClose();
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.order.failed"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="cart-sheet">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] sm:max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">{t("klaer.cart.kicker")}</div>
            <h2 className="text-xl font-black text-slate-900">{t("klaer.cart.title")} <span className="text-slate-400 font-bold">({total})</span></h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cart.items.length === 0 ? (
            <div className="py-12 text-center">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm text-slate-500 italic">{t("klaer.cart.empty")}</div>
            </div>
          ) : (
            <div className="space-y-2">
              {cart.items.map((l) => (
                <div key={l.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`cart-line-${l.id}`}>
                  <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {l.image_url ? (
                      <img src={l.image_url} alt={l.item_name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-slate-900 truncate">{l.item_name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-slate-600">
                      {l.size && <span className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono font-bold">{l.size}</span>}
                      {l.color && <span>{l.color}</span>}
                    </div>
                    {l.note && <div className="text-[11px] text-slate-500 italic truncate mt-0.5">&ldquo;{l.note}&rdquo;</div>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => cart.setQty(l.id, l.quantity - 1)}
                      disabled={l.quantity <= 1}
                      className="w-7 h-7 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                      data-testid={`cart-dec-${l.id}`}
                    >
                      <Minus className="w-3 h-3 text-slate-600" />
                    </button>
                    <span className="w-7 text-center text-sm font-black text-slate-900">{l.quantity}</span>
                    <button
                      onClick={() => cart.setQty(l.id, l.quantity + 1)}
                      className="w-7 h-7 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center"
                      data-testid={`cart-inc-${l.id}`}
                    >
                      <Plus className="w-3 h-3 text-slate-600" />
                    </button>
                    <button
                      onClick={() => cart.removeLine(l.id)}
                      className="ml-1 w-7 h-7 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500"
                      data-testid={`cart-remove-${l.id}`}
                      title={t("klaer.cart.remove")}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="pt-3">
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("klaer.cart.order_note")}</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("klaer.cart.order_note_placeholder")}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="cart-order-note"
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50 rounded-b-2xl">
          <button
            onClick={cart.clear}
            disabled={!cart.items.length || busy}
            className="text-xs font-semibold text-slate-500 hover:text-red-600 disabled:opacity-40"
            data-testid="cart-clear-btn"
          >
            {t("klaer.cart.clear")}
          </button>
          <button
            onClick={submit}
            disabled={!cart.items.length || busy}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black"
            style={{ color: "#fff" }}
            data-testid="cart-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t("klaer.cart.submit_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// ORDERS tab
// -----------------------------------------------------------------
function OrdersTab({ authHeader, isAdmin, refreshKey }) {
  const { t } = useLang();
  const [tab, setTab] = useState("mine"); // mine | incoming (admin only)
  const [statusFilter, setStatusFilter] = useState("all"); // all | new | delivered
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "mine" ? "?mine=true" : "";
      const { data } = await axios.get(`${API}/doserne/clothing/orders${params}`, authHeader());
      setOrders(data.orders || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authHeader, tab]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const visible = useMemo(() => {
    if (statusFilter === "all") return orders;
    return orders.filter((o) => o.status === statusFilter);
  }, [orders, statusFilter]);

  const counts = useMemo(() => ({
    all: orders.length,
    new: orders.filter((o) => o.status === "new").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
  }), [orders]);

  const markDelivered = async (o) => {
    if (!window.confirm(t("klaer.orders.deliver_confirm", { name: o.user_name }))) return;
    try {
      await axios.post(`${API}/doserne/clothing/orders/${o.id}/deliver`, {}, authHeader());
      toast.success(t("klaer.orders.delivered_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.orders.op_failed"));
    }
  };
  const reopen = async (o) => {
    try {
      await axios.post(`${API}/doserne/clothing/orders/${o.id}/reopen`, {}, authHeader());
      toast.info(t("klaer.orders.reopen_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.orders.op_failed"));
    }
  };
  const del = async (o) => {
    if (!window.confirm(t("klaer.orders.delete_confirm"))) return;
    try {
      await axios.delete(`${API}/doserne/clothing/orders/${o.id}`, authHeader());
      toast.success(t("klaer.orders.deleted_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("klaer.orders.op_failed"));
    }
  };

  return (
    <div data-testid="klaer-orders-tab">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {isAdmin && (
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setTab("incoming")}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab === "incoming" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="orders-tab-incoming"
            >
              {t("klaer.orders.tab_incoming")}
            </button>
            <button
              onClick={() => setTab("mine")}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab === "mine" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="orders-tab-mine"
            >
              {t("klaer.orders.tab_mine")}
            </button>
          </div>
        )}

        <div className="inline-flex bg-slate-50 border border-slate-200 rounded-lg p-1">
          {[
            { key: "all", label: t("klaer.orders.filter_all") },
            { key: "new", label: t("klaer.orders.filter_new") },
            { key: "delivered", label: t("klaer.orders.filter_delivered") },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1 rounded text-xs font-black ${statusFilter === key ? (key === "delivered" ? "bg-emerald-600 text-white" : key === "new" ? "bg-amber-500 text-white" : "bg-slate-900 text-white") : "text-slate-600 hover:bg-white"}`}
              style={statusFilter === key ? { color: "#fff" } : {}}
              data-testid={`orders-filter-${key}`}
            >
              {label} <span className="opacity-70 ml-0.5">({counts[key]})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic">
          {statusFilter === "delivered"
            ? t("klaer.orders.empty_delivered")
            : statusFilter === "new"
              ? t("klaer.orders.empty_new")
              : tab === "incoming" ? t("klaer.orders.empty_incoming") : t("klaer.orders.empty_mine")}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((o) => (
            <div key={o.id} className={`bg-white border-2 rounded-2xl p-4 ${o.status === "new" ? "border-amber-200" : "border-slate-200 opacity-80"}`} data-testid={`order-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-black text-slate-900">{o.user_name}</span>
                    {o.status === "new" ? (
                      <span className="text-[9px] uppercase tracking-widest font-black text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">{t("klaer.orders.badge_new")}</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> {t("klaer.orders.badge_delivered")}</span>
                    )}
                    <span className="text-xs text-slate-400">· {fmtWhen(o.created_at)}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="font-semibold">{i.item_name}</span>
                        {i.size && <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">{i.size}</span>}
                        {i.color && <span className="text-slate-500">· {i.color}</span>}
                        <span className="text-slate-500">· {i.quantity} {t("klaer.orders.pcs")}</span>
                        {i.note && <span className="text-xs text-slate-400 italic">({i.note})</span>}
                      </div>
                    ))}
                  </div>
                  {o.note && <div className="mt-2 text-xs text-slate-500 italic">&ldquo;{o.note}&rdquo;</div>}
                  {o.delivered_at && (
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-xs font-black text-emerald-800">{t("klaer.orders.delivered_at", { when: fmtWhen(o.delivered_at) })}</span>
                      {o.delivered_by && <span className="text-[10px] text-emerald-700">{t("klaer.orders.delivered_by", { who: o.delivered_by })}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isAdmin && o.status === "new" && (
                    <button onClick={() => markDelivered(o)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black" style={{ color: "#fff" }} data-testid={`deliver-${o.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("klaer.orders.deliver_btn")}
                    </button>
                  )}
                  {isAdmin && o.status === "delivered" && (
                    <button onClick={() => reopen(o)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" title={t("klaer.orders.reopen_title")}>
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(isAdmin || o.status === "new") && (
                    <button onClick={() => del(o)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-500 border border-red-200" data-testid={`delete-${o.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// PAGE — main entry
// -----------------------------------------------------------------
export default function DoserneKlaer() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("shop");
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState({ sizes: {}, color_pref: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCatalogAdmin, setShowCatalogAdmin] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const cart = useCart(user?.id, authHeader);

  const loadCatalog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/clothing/catalog`, authHeader());
      setCatalog(data.items || []);
      setCategories(data.categories || []);
    } catch { /* ignore */ }
  }, [authHeader]);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/clothing/profile`, authHeader());
      setProfile({ sizes: data.sizes || {}, color_pref: data.color_pref || "" });
    } catch { /* ignore */ }
  }, [authHeader]);

  useEffect(() => { if (user) { loadCatalog(); loadProfile(); } }, [user, loadCatalog, loadProfile]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  const tabs = [
    { key: "shop", label: t("klaer.tab.shop"), Icon: ShoppingBag },
    { key: "profile", label: t("klaer.tab.profile"), Icon: Ruler },
    { key: "orders", label: t("klaer.tab.orders"), Icon: ListChecks },
  ];
  const cartCount = cart.items.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);

  return (
    <DoserneShell>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">{t("klaer.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t("klaer.title")}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowCart(true)}
            className="relative inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-black"
            style={{ color: "#fff" }}
            data-testid="klaer-open-cart-btn"
            title={t("klaer.cart.title")}
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">{t("klaer.cart.title")}</span>
            {cartCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-emerald-500 text-[10px] font-black flex items-center justify-center border-2 border-white"
                style={{ color: "#fff" }}
                data-testid="klaer-cart-badge"
              >
                {cartCount}
              </span>
            )}
          </button>
          {user.is_admin && (
            <button
              onClick={() => setShowCatalogAdmin(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-sm font-semibold"
              title={t("klaer.admin_catalog_btn")}
              data-testid="klaer-catalog-admin-btn"
            >
              <Settings2 className="w-4 h-4" /> <span className="hidden sm:inline">{t("klaer.catalog")}</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === key ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`klaer-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "shop" && <ShopTab catalog={catalog} categories={categories} authHeader={authHeader} profile={profile} onAddToCart={cart.add} />}
      {tab === "profile" && <SizeProfileTab catalog={catalog} authHeader={authHeader} />}
      {tab === "orders" && <OrdersTab authHeader={authHeader} isAdmin={!!user.is_admin} refreshKey={refreshKey} />}

      {showCatalogAdmin && (
        <CatalogAdmin
          onClose={() => setShowCatalogAdmin(false)}
          authHeader={authHeader}
          onChanged={loadCatalog}
        />
      )}

      {showCart && (
        <CartSheet
          cart={cart}
          onClose={() => setShowCart(false)}
          authHeader={authHeader}
          onSubmitted={() => { setRefreshKey((k) => k + 1); setTab("orders"); }}
        />
      )}

      {/* Floating cart FAB on mobile when items are in the cart */}
      {cartCount > 0 && !showCart && (
        <button
          onClick={() => setShowCart(true)}
          className="sm:hidden fixed bottom-24 right-4 z-40 inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 shadow-xl shadow-emerald-500/40 border-4 border-white font-black text-sm"
          style={{ color: "#fff" }}
          data-testid="klaer-cart-fab"
        >
          <ShoppingCart className="w-5 h-5" />
          <span>{cartCount}</span>
        </button>
      )}
    </DoserneShell>
  );
}
